import React from 'react'
import dayjs from 'dayjs'

/**
 * Renders completed assessment run details for the report print/preview.
 * Shown when Assessment Test Launcher EMR section is enabled for print.
 * @param {Object} props
 * @param {Array} props.assessmentRuns - List of run objects from getAssessmentRuns (completed runs when includeDrafts=false)
 * @param {string} [props.sectionTitle] - Title for the section (e.g. from EMR section)
 */
function getRunTitle(run) {
  return (
    run?.assessmentTitle ??
    run?.assessmentRunView?.assessmentTitle ??
    run?.assessmentRun?.assessmentTitle ??
    'Assessment'
  )
}

function getRunCompletedAt(run) {
  const at =
    run?.completedAt ??
    run?.assessmentRunView?.completedAt ??
    run?.assessmentRun?.completedAt ??
    run?.run?.completedAt
  if (at == null) return null
  const date = typeof at === 'number' ? (at < 1e12 ? new Date(at * 1000) : new Date(at)) : new Date(at)
  return dayjs(date).format('DD/MM/YYYY HH:mm')
}

function getRunnerType(run) {
  return String(run?.runnerType ?? run?.assessmentRunView?.runnerType ?? run?.assessmentRun?.runnerType ?? '')
    .trim()
    .toUpperCase()
}

function isCustomProtocolRun(run) {
  return getRunnerType(run) === 'CUSTOM_PROTOCOL'
}

function getDerivedMetrics(run) {
  return run?.derivedMetrics ?? run?.assessmentRunView?.derivedMetrics ?? run?.assessmentRun?.derivedMetrics ?? run?.run?.derivedMetrics ?? {}
}

function getScoringTrace(run) {
  return run?.scoringTrace ?? run?.assessmentRunView?.scoringTrace ?? run?.assessmentRun?.scoringTrace ?? run?.run?.scoringTrace ?? {}
}

function getDomainResults(run) {
  return run?.domainResults ?? run?.assessmentRunView?.domainResults ?? run?.assessmentRun?.domainResults ?? run?.run?.domainResults ?? []
}

function getRunFlags(run) {
  const flags = run?.flags ?? run?.assessmentRunView?.flags ?? run?.assessmentRun?.flags ?? run?.run?.flags ?? []
  return Array.isArray(flags) ? flags : []
}

function isDstProtocolRun(run) {
  const protocolType = getDerivedMetrics(run)?.protocolType
  return String(protocolType ?? '').trim().toUpperCase() === 'DST'
}

function formatLabel(label) {
  if (!label) return ''
  const mapped = {
    protocolType: 'Protocol',
    formulaType: 'Formula',
    scoringMode: 'Scoring mode',
    scorePercentage: 'Score percentage',
    completionRate: 'Completion rate',
    starterAdaptiveScore: 'Adaptive score',
    starterDevelopmentalScore: 'Developmental score',
    achievedScore: 'Achieved score',
    totalPossibleScore: 'Total possible score',
    totalItems: 'Total items',
    answeredItems: 'Answered items',
    passCount: 'PASS count',
    failCount: 'FAIL count',
    notTestableCount: 'Not testable count',
    contentNotice: 'Scoring note',
    ageSourceLabel: 'Age source',
    estimatedDateOfBirth: 'Estimated DOB',
    providedDateOfBirth: 'Provided DOB',
    dateOfBirthOverride: 'Practitioner override DOB',
    dateOfBirthUsed: 'DOB used for scoring',
    developmentalQuotient: 'Developmental Quotient',
    chronologicalAgeDisplay: 'Chronological age',
    developmentalAgeDisplay: 'Developmental age',
    actualAgeBand: 'Actual age band',
    recommendedStartBand: 'Recommended start band',
    highestPassedMilestoneAgeDisplay: 'Highest passed age',
    maxConsecutiveFailCount: 'Max consecutive FAIL',
    maxConsecutiveFailEndAgeDisplay: 'Ceiling reached at',
    developmentalAgeMilestone: 'Developmental age milestone',
    highestPassedMilestone: 'Highest passed milestone',
    firstCeilingFailure: 'First ceiling failure',
  }
  if (mapped[label]) return mapped[label]
  return String(label)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase())
}

function formatMetricValue(key, value) {
  if (value == null) return null
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  const text = String(value).trim()
  if (!text) return null
  if (['estimatedDateOfBirth', 'providedDateOfBirth', 'dateOfBirthOverride', 'dateOfBirthUsed'].includes(key)) {
    const parsed = dayjs(text)
    if (parsed.isValid()) return parsed.format('DD MMM YYYY')
  }
  if (['scorePercentage', 'completionRate'].includes(key) && !text.endsWith('%')) {
    return `${text}%`
  }
  return text
}

function buildMetricRows(run) {
  const derivedMetrics = getDerivedMetrics(run)
  const priorityKeys = [
    'protocolType',
    'formulaType',
    'scoringMode',
    'starterAdaptiveScore',
    'starterDevelopmentalScore',
    'scorePercentage',
    'completionRate',
    'achievedScore',
    'totalPossibleScore',
    'totalItems',
    'answeredItems',
    'passCount',
    'failCount',
    'notTestableCount',
    'ageSourceLabel',
    'estimatedDateOfBirth',
    'providedDateOfBirth',
    'dateOfBirthOverride',
    'dateOfBirthUsed',
  ]
  const excludedKeys = new Set([
    'contentNotice',
    'developmentalQuotientInterpretation',
    'ageRangeGuidance',
    'developmentalAgeMilestone',
    'highestPassedMilestone',
    'highestPassedMilestoneAgeDisplay',
    'firstCeilingFailure',
    'firstCeilingFailureAgeDisplay',
    'actualAgeBand',
    'recommendedStartBand',
    'chronologicalAgeDisplay',
    'developmentalAgeDisplay',
    'developmentalQuotient',
    'maxConsecutiveFailCount',
    'maxConsecutiveFailEndAgeDisplay',
  ])

  const rows = []
  for (const key of priorityKeys) {
    const formatted = formatMetricValue(key, derivedMetrics?.[key])
    if (formatted != null) rows.push({ key, label: formatLabel(key), value: formatted })
  }
  Object.entries(derivedMetrics ?? {}).forEach(([key, value]) => {
    if (priorityKeys.includes(key) || excludedKeys.has(key)) return
    if (typeof value === 'object') return
    const formatted = formatMetricValue(key, value)
    if (formatted != null) rows.push({ key, label: formatLabel(key), value: formatted })
  })
  return rows
}

function getDstSummaryCards(run) {
  const derivedMetrics = getDerivedMetrics(run)
  return [
    { key: 'chronologicalAgeDisplay', label: 'Chronological age', value: derivedMetrics?.chronologicalAgeDisplay ?? 'Not set' },
    { key: 'developmentalAgeDisplay', label: 'Developmental age', value: derivedMetrics?.developmentalAgeDisplay ?? 'Not set' },
    { key: 'developmentalQuotient', label: 'Developmental Quotient', value: derivedMetrics?.developmentalQuotient ?? 'Not scored' },
    { key: 'actualAgeBand', label: 'Actual age band', value: derivedMetrics?.actualAgeBand ?? 'Not set' },
    { key: 'recommendedStartBand', label: 'Recommended start band', value: derivedMetrics?.recommendedStartBand ?? 'Not set' },
    { key: 'highestPassedMilestoneAgeDisplay', label: 'Highest passed age', value: derivedMetrics?.highestPassedMilestoneAgeDisplay ?? 'Not set' },
    { key: 'maxConsecutiveFailCount', label: 'Max consecutive FAIL', value: String(derivedMetrics?.maxConsecutiveFailCount ?? 0) },
    { key: 'maxConsecutiveFailEndAgeDisplay', label: 'Ceiling reached at', value: derivedMetrics?.maxConsecutiveFailEndAgeDisplay ?? 'Not reached yet' },
  ]
}

function getDstDobAuditRows(run) {
  const derivedMetrics = getDerivedMetrics(run)
  return [
    { key: 'ageSourceLabel', label: 'Age source', value: derivedMetrics?.ageSourceLabel ?? null },
    { key: 'estimatedDateOfBirth', label: 'Estimated DOB', value: formatMetricValue('estimatedDateOfBirth', derivedMetrics?.estimatedDateOfBirth) },
    { key: 'providedDateOfBirth', label: 'Provided DOB', value: formatMetricValue('providedDateOfBirth', derivedMetrics?.providedDateOfBirth) },
    { key: 'dateOfBirthOverride', label: 'Practitioner override DOB', value: formatMetricValue('dateOfBirthOverride', derivedMetrics?.dateOfBirthOverride) },
    { key: 'dateOfBirthUsed', label: 'DOB used for scoring', value: formatMetricValue('dateOfBirthUsed', derivedMetrics?.dateOfBirthUsed) },
  ].filter((item) => item.value)
}

function getDstStageSummaries(run) {
  const stageBreakdown = getScoringTrace(run)?.stageBreakdown
  if (!stageBreakdown || typeof stageBreakdown !== 'object') return []
  return Object.values(stageBreakdown).filter(Boolean)
}

function getDstHighlightedMilestones(run) {
  const derivedMetrics = getDerivedMetrics(run)
  return [
    {
      key: 'developmentalAgeMilestone',
      label: 'Developmental age milestone',
      value: derivedMetrics?.developmentalAgeMilestone ?? null,
      secondary: null,
    },
    {
      key: 'highestPassedMilestone',
      label: 'Highest passed milestone',
      value: derivedMetrics?.highestPassedMilestone ?? null,
      secondary: derivedMetrics?.highestPassedMilestoneAgeDisplay ?? null,
    },
    {
      key: 'firstCeilingFailure',
      label: 'First ceiling failure',
      value: derivedMetrics?.firstCeilingFailure ?? null,
      secondary: derivedMetrics?.firstCeilingFailureAgeDisplay ?? null,
    },
  ].filter((item) => item.value)
}

function getDstResponseRows(run) {
  const rows = getScoringTrace(run)?.responseTableRows
  return Array.isArray(rows) ? rows : []
}

function getDomainBreakdownRows(run) {
  const domainBreakdown = getScoringTrace(run)?.domainBreakdown
  if (!domainBreakdown || typeof domainBreakdown !== 'object') return []
  return Object.values(domainBreakdown).filter(Boolean)
}

function renderQuestionnaireRuns(runs) {
  if (!Array.isArray(runs) || runs.length === 0) return null
  return (
    <ul style={{ margin: 0, paddingLeft: '1.5em', lineHeight: 1.6 }}>
      {runs.map((run, idx) => {
        const title = getRunTitle(run)
        const completedAt = getRunCompletedAt(run)
        const key = run?.assessmentRunId ?? run?.id ?? `run-${idx}`
        return (
          <li key={key} style={{ marginBottom: '0.25em' }}>
            <strong>{title}</strong>
            {completedAt != null && (
              <span style={{ color: '#64748b', marginLeft: '0.5em' }}>— Completed: {completedAt}</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function renderCustomRunCard(run, idx) {
  const title = getRunTitle(run)
  const completedAt = getRunCompletedAt(run)
  const key = run?.assessmentRunId ?? run?.id ?? `custom-run-${idx}`
  const derivedMetrics = getDerivedMetrics(run)
  const flags = getRunFlags(run)
  const contentNotice = derivedMetrics?.contentNotice
  const isDstRun = isDstProtocolRun(run)
  const metricRows = buildMetricRows(run)
  const domainResults = Array.isArray(getDomainResults(run)) ? getDomainResults(run) : []
  const domainBreakdownRows = getDomainBreakdownRows(run)
  const dstSummaryCards = isDstRun ? getDstSummaryCards(run) : []
  const dstDobAuditRows = isDstRun ? getDstDobAuditRows(run) : []
  const dstStageSummaries = isDstRun ? getDstStageSummaries(run) : []
  const dstHighlightedMilestones = isDstRun ? getDstHighlightedMilestones(run) : []
  const dstResponseRows = isDstRun ? getDstResponseRows(run) : []

  return (
    <div
      key={key}
      style={{
        border: '1px solid #dbe4f0',
        borderRadius: 14,
        padding: '16px',
        marginBottom: '14px',
        background: '#ffffff',
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline', marginBottom: '10px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{title}</div>
        {completedAt != null && (
          <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'right' }}>Completed: {completedAt}</div>
        )}
      </div>

      {contentNotice && (
        <div style={{ marginBottom: '10px', color: '#475569', fontSize: '13px', lineHeight: 1.6 }}>
          {contentNotice}
        </div>
      )}

      {flags.length > 0 && (
        <div style={{ marginBottom: '12px', color: '#475569', fontSize: '12px' }}>
          <strong>Flags:</strong> {flags.join(', ')}
        </div>
      )}

      {isDstRun ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '10px',
              marginBottom: '12px',
            }}
          >
            {dstSummaryCards.map((card) => (
              <div key={card.key} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', background: '#f8fafc' }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{card.label}</div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{String(card.value)}</div>
              </div>
            ))}
          </div>

          {derivedMetrics?.developmentalQuotientInterpretation && (
            <div style={{ marginBottom: '10px', color: '#334155', fontSize: '13px', lineHeight: 1.6 }}>
              <strong>Interpretation:</strong> {derivedMetrics.developmentalQuotientInterpretation}
            </div>
          )}

          {derivedMetrics?.ageRangeGuidance && (
            <div style={{ marginBottom: '10px', color: '#92400e', fontSize: '13px', lineHeight: 1.6 }}>
              <strong>Age range guidance:</strong> {derivedMetrics.ageRangeGuidance}
            </div>
          )}

          {dstDobAuditRows.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>Age reference used</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px 12px' }}>
                {dstDobAuditRows.map((row) => (
                  <div key={row.key}>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{row.label}</div>
                    <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>{row.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dstHighlightedMilestones.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '12px' }}>
              {dstHighlightedMilestones.map((milestone) => (
                <div key={milestone.key} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{milestone.label}</div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{milestone.value}</div>
                  {milestone.secondary && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{milestone.secondary}</div>}
                </div>
              ))}
            </div>
          )}

          {dstStageSummaries.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>Stage summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {dstStageSummaries.map((stage, stageIndex) => (
                  <div key={stage?.domainId ?? `stage-${stageIndex}`} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                      {stage?.stageTitle ?? stage?.domainId ?? 'Stage'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      Answered {stage?.answeredItems ?? 0} / {stage?.totalItems ?? 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      PASS {stage?.passCount ?? 0} | FAIL {stage?.failCount ?? 0} | Not testable {stage?.notTestableCount ?? 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dstResponseRows.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>DST response ledger</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {['Stage', 'Milestone', 'Age Eq', 'Status', 'Evidence', 'Notes'].map((label) => (
                        <th
                          key={label}
                          style={{
                            border: '1px solid #dbe4f0',
                            padding: '6px 8px',
                            textAlign: 'left',
                            background: '#eef4ff',
                            color: '#1e293b',
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dstResponseRows.map((row, rowIndex) => (
                      <tr key={`${row?.itemCode ?? row?.milestone ?? 'row'}-${rowIndex}`}>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{row?.stageTitle ?? '-'}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>
                          {row?.milestone ?? '-'}
                          {row?.starredInSource ? ' *' : ''}
                        </td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{row?.ageEquivalentDisplay ?? '-'}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{String(row?.status ?? '-').replaceAll('_', ' ')}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{String(row?.evidence ?? '-').replaceAll('_', ' ')}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 8px' }}>{row?.notes ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {metricRows.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px 12px', marginBottom: '12px' }}>
              {metricRows.map((row) => (
                <div key={row.key}>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>{row.label}</div>
                  <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>{row.value}</div>
                </div>
              ))}
            </div>
          )}

          {domainResults.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>Domain results</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {domainResults.map((domain, domainIndex) => (
                  <div key={domain?.domainId ?? `domain-${domainIndex}`} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                      {domain?.domainName ?? domain?.domainId ?? 'Domain'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      Score: {domain?.userScore ?? '-'} / {domain?.totalScore ?? '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {domainBreakdownRows.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>Scoring breakdown</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {domainBreakdownRows.map((domain, domainIndex) => (
                  <div key={domain?.domainTitle ?? `breakdown-${domainIndex}`} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                      {domain?.domainTitle ?? 'Domain'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      Answered {domain?.answeredItems ?? 0} / {domain?.totalItems ?? 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>
                      PASS {domain?.passCount ?? 0} | FAIL {domain?.failCount ?? 0} | Not testable {domain?.notTestableCount ?? 0}
                    </div>
                    {domain?.scorePercentage != null && (
                      <div style={{ fontSize: '12px', color: '#475569' }}>
                        Score: {domain.scorePercentage}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default function AssessmentReportRunDetails({ assessmentRuns = [], sectionTitle = 'Assessment Run Details' }) {
  const runs = Array.isArray(assessmentRuns) ? assessmentRuns : []
  const completed = runs.filter((r) => getRunCompletedAt(r) != null)
  const customRuns = completed.filter((run) => isCustomProtocolRun(run))
  const questionnaireRuns = completed.filter((run) => !isCustomProtocolRun(run))

  if (completed.length === 0) return null

  return (
    <div
      className="assessment-report-section assessment-report-run-details"
      style={{ marginBottom: '1em', wordWrap: 'break-word', wordBreak: 'break-word' }}
    >
      <h2
        style={{
          textDecoration: 'underline',
          display: 'block',
          marginBottom: '0.75em',
          marginTop: 0,
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b',
          pageBreakAfter: 'avoid',
        }}
      >
        {sectionTitle}
      </h2>
      {customRuns.length > 0 && (
        <div style={{ marginBottom: questionnaireRuns.length > 0 ? '1rem' : 0 }}>
          {customRuns.map((run, idx) => renderCustomRunCard(run, idx))}
        </div>
      )}
      {questionnaireRuns.length > 0 && renderQuestionnaireRuns(questionnaireRuns)}
    </div>
  )
}
